import { defineStore, storeToRefs } from 'pinia';
import { useCookies } from 'vue3-cookies';
import { getDomain } from '@/utils/util';
import {
  getUserInfo,
  postUserUpdate,
  postReportPayment,
  getUserPortalLink,
} from '@service/user';
import avator from '@utils/avator';
import usePopMessageStore from '@store/popMessage';
import useSystemStore from '@store/system';
import useAuthStore from '@store/auth';

type pay_cycle = 'month' | 'year';
const { cookies } = useCookies();
const name = 'user';
const useUserStore = defineStore(name, {
  state: () =>
    ({
      uuid: '' as string,
      uid: '' as string,
      credit_limit: 0 as number,
      invite_count: 0 as number,
      voice_preview_limit: 0 as number,
      subscribe_info: {} as Subscribeinfo,
      credit_state: {} as Creditstate,
      account_info: {} as AccountInfo,
      avatar: '' as string,
      name: '' as string,
      nick_name: '' as string,
      verified: 0 as number,
      credit_total: 0 as number,
      currentPlan: null as ProductItem | null,
      pay_cycle: 'month' as 'month' | 'year',
      plans: [] as ProductItem[],
      is_vip: false as boolean,
    } as UserInfo),

  actions: {
    async getUserInfo() {
      const authStore = useAuthStore();
      const { isLoggedIn } = storeToRefs(authStore);

      if (!isLoggedIn.value) {
        await this.getCurrentPlan();
        return;
      }

      const res = await getUserInfo();

      if (res) {
        const { subscribe_info, name, uuid } = res;
        this.$state = res;
        this.is_vip = subscribe_info?.sku_id ? true : false;

        // 给GOOGLE上报，用于广告投放使用，一旦订阅，修改server状态
        if (subscribe_info?.report_status === 0) {
          const data = await postReportPayment();
          window.dataLayer.push({
            event: 'done_payment',
            ecommerce: {
              transaction_id: data?.order_id,
              value: data?.price,
              currency: data?.currency,
            },
          });
        }

        // 没有图片生成一个
        if (!name) {
          this.avatar = avator('U', 0);
          this.name = 'User';
        }

        if (subscribe_info.sub_group_id === '2') {
          this.pay_cycle = 'year';
        }

        await this.getCurrentPlan();
        cookies.set('uuid', uuid, 30 * 24 * 60 * 60, '/', getDomain());
      }
    },

    async getCurrentPlan(isSwitch?: boolean) {
      let isUpgrade = false;
      const systemStore = useSystemStore();
      const { sku_id, sub_group_id } = this.subscribe_info;
      const { credit_product_list_new } = storeToRefs(systemStore);

      const product_cycle =
        credit_product_list_new.value?.credit[this.pay_cycle];
      const product_partner = credit_product_list_new.value?.partner?.demand;

      // 最高等级
      product_cycle[product_cycle.length - 1].is_top_plan = true;
      const products = [...product_cycle, ...product_partner];

      // 订阅如果是月，点击tab为year，全都是 升级
      if (sub_group_id === '1' && this.pay_cycle === 'year') {
        isUpgrade = true;
      }

      const plan = products?.filter((item: ProductItem) => {
        const hasSub = item.sku_id === sku_id;

        item.hasSub = hasSub;
        item.mark_word.name = item?.mark_word?.name.toLocaleLowerCase();

        // 年的套餐，不能降级为 月
        if (sub_group_id === '2' && this.pay_cycle === 'month') {
          item.disable = true;
        }

        if (isUpgrade) {
          item.isUpgrade = true;
        } else {
          item.isUpgrade = false;
        }

        if (hasSub) {
          isUpgrade = true;
        }

        return hasSub;
      });

      this.plans = products;

      if (!isSwitch) {
        if (plan?.length) {
          this.currentPlan = plan[0];
        } else {
          this.currentPlan = products[0];
        }
      }
    },

    async updateUser(params: {
      name?: string;
      avatar_fid?: string;
      usage_billing?: number;
    }) {
      const popMessageStore = usePopMessageStore();
      const status = await postUserUpdate({
        ...params,
      });

      if (!status) {
        let message = '';

        if (params?.usage_billing?.toString()) {
          message = params.usage_billing
            ? 'msg.success.binding'
            : 'msg.success.unbinding';
        } else if (params?.avatar_fid) {
          message = 'msg.success.profile';
        } else if (params?.name) {
          message = 'msg.success.username';
        }

        if (message) {
          popMessageStore.show({
            type: 'success',
            message,
          });
          this.getUserInfo();
        }
      }
    },

    getLevelLimit(groudIndex: number) {
      const { sub_group_index = 0 } = this.subscribe_info;
      return !(sub_group_index >= groudIndex);
    },

    async goPersonLink(isBlank?: boolean) {
      const res = await getUserPortalLink();
      if (res?.portal_url) {
        if (isBlank) {
          window.open(res?.portal_url, '_blank');
        } else {
          location.href = res?.portal_url;
        }
      }
    },
  },
});

export default useUserStore;
